import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Chat types
export const CHAT_TYPES = {
  LINKEDIN_AI: 'linkedin-ai',
  GENERAL_CHAT: 'general-chat',
};

// Connection states
export const CONNECTION_STATES = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  ERROR: 'error',
};

// Message types
export const MESSAGE_TYPES = {
  USER: 'user',
  AI: 'ai',
  SYSTEM: 'system',
  TYPING: 'typing',
};

// Initial state
const initialState = {
  // Chat identification
  chatId: null,
  chatType: CHAT_TYPES.LINKEDIN_AI,

  // Messages
  messages: [],

  // Connection state
  connectionState: CONNECTION_STATES.DISCONNECTED,
  connectionError: null,

  // UI state
  isTyping: false,
  isSending: false,

  // WebSocket
  wsConnection: null,
  reconnectAttempts: 0,
  maxReconnectAttempts: 5,

  // Error handling
  error: null,
  lastError: null,
};

// Generate unique chat ID
const generateChatId = () => {
  return `linkedin-ai-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Async thunks
export const sendLinkedInAIMessage = createAsyncThunk(
  'sendMessage',
  async (messageData, { rejectWithValue, getState, dispatch }) => {
    try {
      const { message, timestamp, chatId } = messageData;
      const state = getState();
      const { wsConnection } = state.linkedinChat;
      const currentLanguage = state.language.lang;

      // Use existing chatId or generate new one
      const currentChatId = chatId || state.chatId || generateChatId();

      // Check if WebSocket is connected
      if (!wsConnection || wsConnection.readyState !== WebSocket.OPEN) {
        throw new Error('WebSocket bağlantısı yok');
      }

      // Send message via WebSocket
      const messagePayload = {
        action: 'chat',
        message: message,
        language: currentLanguage,
      };

      wsConnection.send(JSON.stringify(messagePayload));

      // Set typing indicator
      dispatch(setTyping(true));

      // Return success (actual response will come via WebSocket onmessage)
      return {
        success: true,
        message: 'Message sent',
        timestamp: new Date().toISOString(),
        chatId: currentChatId,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'LinkedIn AI mesajı gönderilemedi');
    }
  },
);

export const connectLinkedInAIWebSocket = createAsyncThunk(
  'connectWebSocket',
  async (_, { rejectWithValue, dispatch, getState }) => {
    try {
      const state = getState().linkedinChat;
      const chatId = state.chatId || generateChatId();
      const wsUrl = process.env.REACT_APP_LINKEDIN_AI_CHAT_BOT_URL;

      if (!wsUrl) {
        throw new Error('WebSocket URL not configured');
      }

      return new Promise((resolve, reject) => {
        const ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          // Set up message handler
          ws.onmessage = (event) => {
            try {
              const data = JSON.parse(event.data);

              // Handle different message types
              // Only show chat messages, not connect/disconnect system messages
              if (data.message) {
                dispatch(
                  addMessage({
                    type: MESSAGE_TYPES.AI,
                    text: data.message,
                    timestamp: new Date(),
                  }),
                );
                dispatch(setTyping(false));
              }
            } catch (error) {
              console.error('Error parsing WebSocket message:', error);
            }
          };

          ws.onerror = (error) => {
            console.error('WebSocket error:', error);
            dispatch(setConnectionState(CONNECTION_STATES.ERROR));
          };

          ws.onclose = (event) => {
            dispatch(setConnectionState(CONNECTION_STATES.DISCONNECTED));
          };

          resolve({
            connected: true,
            chatId: chatId,
            chatType: CHAT_TYPES.LINKEDIN_AI,
            wsConnection: ws,
          });
        };

        ws.onerror = (error) => {
          console.error('WebSocket connection error:', error);
          reject(new Error('WebSocket bağlantısı kurulamadı'));
        };

        // Timeout after 10 seconds
        setTimeout(() => {
          if (ws.readyState !== WebSocket.OPEN) {
            ws.close();
            reject(new Error('WebSocket bağlantı zaman aşımı'));
          }
        }, 10000);
      });
    } catch (error) {
      return rejectWithValue(error.message || 'LinkedIn AI WebSocket bağlantısı kurulamadı');
    }
  },
);

export const disconnectLinkedInAIWebSocket = createAsyncThunk('disconnectWebSocket', async (_, { getState }) => {
  const { wsConnection } = getState().linkedinChat;

  if (wsConnection && wsConnection.readyState === WebSocket.OPEN) {
    // Send disconnect action
    wsConnection.close();
  }

  return { disconnected: true };
});

// LinkedIn AI Chat slice
const linkedinChatSlice = createSlice({
  name: 'linkedinChat',
  initialState,
  reducers: {
    // Chat identification
    setChatId: (state, action) => {
      state.chatId = action.payload;
    },

    setChatType: (state, action) => {
      state.chatType = action.payload;
    },

    // Message actions
    addMessage: (state, action) => {
      const { type, text, timestamp, id } = action.payload;
      state.messages.push({
        id: id || Date.now(),
        type,
        text,
        timestamp: timestamp || new Date(),
        chatId: state.chatId,
      });
    },

    clearMessages: (state) => {
      state.messages = [];
    },

    updateMessage: (state, action) => {
      const { id, updates } = action.payload;
      const messageIndex = state.messages.findIndex((msg) => msg.id === id);
      if (messageIndex !== -1) {
        state.messages[messageIndex] = { ...state.messages[messageIndex], ...updates };
      }
    },

    removeMessage: (state, action) => {
      const messageId = action.payload;
      state.messages = state.messages.filter((msg) => msg.id !== messageId);
    },

    // Typing actions
    setTyping: (state, action) => {
      state.isTyping = action.payload;
    },

    setSending: (state, action) => {
      state.isSending = action.payload;
    },

    // Connection actions
    setConnectionState: (state, action) => {
      state.connectionState = action.payload;
    },

    setConnectionError: (state, action) => {
      state.connectionError = action.payload;
    },

    setReconnectAttempts: (state, action) => {
      state.reconnectAttempts = action.payload;
    },

    // Error actions
    setError: (state, action) => {
      state.error = action.payload;
      state.lastError = action.payload;
    },

    clearError: (state) => {
      state.error = null;
    },

    // WebSocket actions
    setWebSocketConnection: (state, action) => {
      state.wsConnection = action.payload;
    },

    // Initialize LinkedIn AI chat
    initializeLinkedInAIChat: (state, action) => {
      const { welcomeMessage } = action.payload;

      // Sadece hiç initialize edilmemişse çalış
      if (!state.chatId && state.messages.length === 0) {
        state.chatId = generateChatId();
        state.chatType = CHAT_TYPES.LINKEDIN_AI;
        state.messages.push({
          id: Date.now(),
          type: MESSAGE_TYPES.AI,
          text: welcomeMessage,
          timestamp: new Date(),
          chatId: state.chatId,
        });
      }
    },

    // Reset chat
    resetLinkedInAIChat: (state) => {
      return {
        ...initialState,
        chatId: generateChatId(),
        chatType: CHAT_TYPES.LINKEDIN_AI,
      };
    },
  },
  extraReducers: (builder) => {
    // Send message
    builder
      .addCase(sendLinkedInAIMessage.pending, (state) => {
        state.isSending = true;
        state.error = null;
      })
      .addCase(sendLinkedInAIMessage.fulfilled, (state, action) => {
        state.isSending = false;
        // isTyping will be set to false when AI response comes via WebSocket

        // Update chatId if provided
        if (action.payload.chatId) {
          state.chatId = action.payload.chatId;
        }

        // AI response will be added via WebSocket onmessage handler
        // No need to add it here anymore
      })
      .addCase(sendLinkedInAIMessage.rejected, (state, action) => {
        state.isSending = false;
        state.isTyping = false;
        state.error = action.payload;
      });

    // Connect WebSocket
    builder
      .addCase(connectLinkedInAIWebSocket.pending, (state) => {
        state.connectionState = CONNECTION_STATES.CONNECTING;
        state.connectionError = null;
      })
      .addCase(connectLinkedInAIWebSocket.fulfilled, (state, action) => {
        state.connectionState = CONNECTION_STATES.CONNECTED;
        state.reconnectAttempts = 0;
        state.connectionError = null;
        state.chatId = action.payload.chatId;
        state.chatType = action.payload.chatType;
        state.wsConnection = action.payload.wsConnection;
      })
      .addCase(connectLinkedInAIWebSocket.rejected, (state, action) => {
        state.connectionState = CONNECTION_STATES.ERROR;
        state.connectionError = action.payload;
      });

    // Disconnect WebSocket
    builder.addCase(disconnectLinkedInAIWebSocket.fulfilled, (state) => {
      state.connectionState = CONNECTION_STATES.DISCONNECTED;
      state.wsConnection = null;
      state.connectionError = null;
    });
  },
});

// Export actions
export const {
  setChatId,
  setChatType,
  addMessage,
  clearMessages,
  updateMessage,
  removeMessage,
  setTyping,
  setSending,
  setConnectionState,
  setConnectionError,
  setReconnectAttempts,
  setError,
  clearError,
  setWebSocketConnection,
  initializeLinkedInAIChat,
  resetLinkedInAIChat,
} = linkedinChatSlice.actions;

// Selectors
export const selectLinkedInAIMessages = (state) => state.linkedinChat.messages;
export const selectLinkedInAIChatId = (state) => state.linkedinChat.chatId;
export const selectLinkedInAIChatType = (state) => state.linkedinChat.chatType;
export const selectLinkedInAIConnectionState = (state) => state.linkedinChat.connectionState;
export const selectLinkedInAIIsTyping = (state) => state.linkedinChat.isTyping;
export const selectLinkedInAIIsSending = (state) => state.linkedinChat.isSending;
export const selectLinkedInAIConnectionError = (state) => state.linkedinChat.connectionError;
export const selectLinkedInAIError = (state) => state.linkedinChat.error;
export const selectLinkedInAIReconnectAttempts = (state) => state.linkedinChat.reconnectAttempts;

export default linkedinChatSlice.reducer;
